import { useState } from "react";

import { Controller, FormProvider, useForm } from "react-hook-form";

import { useMutation } from "@tanstack/react-query";

import { zodResolver } from "@hookform/resolvers/zod";

import { EyeIcon, EyeOffIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";

import { SignUpSchema, signUpSchema } from "@/features/auth/schemas";
import Error from "@/shared/components/Error";
import { useAuthentication } from "@/shared/hooks/useAuthentication";
import { signUp } from "@/features/auth/services/auth";
import { AppError } from "@/shared/lib/utils/errors";

export default function SignUpForm() {
  const { authenticate } = useAuthentication();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const form = useForm<SignUpSchema>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      emailAddress: "",
      password: "",
      confirmPassword: "",
    },
  });

  const mutation = useMutation<string, AppError | Error, SignUpSchema>({
    mutationFn: signUp,
    onSuccess: (session) => {
      authenticate(session);
      window.location.href = "/";
    },
  });

  async function onSubmit(values: SignUpSchema) {
    mutation.mutate(values);
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FieldGroup>
          {mutation.isError && <Error title="Unable to create your account" error={mutation.error} />}
          <div className="grid grid-cols-2 gap-4">
            <Controller
              name="firstName"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor={field.name}>First name</FieldLabel>
                  <Input {...field} disabled={mutation.isPending || mutation.isSuccess} aria-invalid={fieldState.invalid} placeholder="John" autoComplete="off"></Input>
                  {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                </Field>
              )}
            ></Controller>
            <Controller
              name="lastName"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor={field.name}>Last name</FieldLabel>
                  <Input {...field} disabled={mutation.isPending || mutation.isSuccess} aria-invalid={fieldState.invalid} placeholder="Smith" autoComplete="off"></Input>
                  {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                </Field>
              )}
            ></Controller>
          </div>
          <Controller
            name="emailAddress"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={field.name}>Email address</FieldLabel>
                <Input {...field} disabled={mutation.isPending || mutation.isSuccess} aria-invalid={fieldState.invalid} placeholder="john.smith@example.com" autoComplete="off"></Input>
                {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
              </Field>
            )}
          ></Controller>
          <Controller
            name="password"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={field.name}>Password</FieldLabel>
                <div className="relative">
                  <Input {...field} disabled={mutation.isPending || mutation.isSuccess} aria-invalid={fieldState.invalid} placeholder="••••••••" autoComplete="off" type={showPassword ? "text" : "password"} className="pr-10"></Input>
                  <button type="button" onClick={() => setShowPassword((p) => !p)} aria-label={showPassword ? "Hide password" : "Show password"} className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground transition-colors">
                    {showPassword ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
                  </button>
                </div>
                <FieldDescription>At least 8 characters, including a number and a symbol.</FieldDescription>
                {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
              </Field>
            )}
          ></Controller>
          <Controller
            name="confirmPassword"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={field.name}>Confirm password</FieldLabel>
                <div className="relative">
                  <Input {...field} disabled={mutation.isPending || mutation.isSuccess} aria-invalid={fieldState.invalid} placeholder="••••••••" autoComplete="off" type={showConfirmPassword ? "text" : "password"} className="pr-10"></Input>
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((p) => !p)}
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showConfirmPassword ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
                  </button>
                </div>
                {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
              </Field>
            )}
          ></Controller>
          <Button type="submit" className="w-full mt-1" disabled={mutation.isPending || mutation.isSuccess}>
            {mutation.isPending || mutation.isSuccess ? "Signing up..." : "Create account"}
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            By creating an account you agree to our <span className="underline underline-offset-4">Terms of Service</span> and <span className="underline underline-offset-4">Privacy Policy</span> .
          </p>
        </FieldGroup>
      </form>
    </FormProvider>
  );
}
